import {
  CLCustomButton,
  CLPrimaryCustomButton,
  CLPrimaryCustomTextarea
} from '@/components/ui-controls';
import CLTooltip from '@/components/utils/tooltip';
import CommonConstants from '@/constants/common-constants';
import { GET_PUBLIC_RUN_CHECKLIST_COMMENTS_BY_CHECKLIST_ID_QUERY } from '@/graphql/checklist-comments.gql';
import { downloadFile } from '@/services/axios/dowload-file';
import { convertISOToLocalDateString } from '@/services/utils/utils.service';
import { useOrganizationStore } from '@/stores/organization.store';
import { usePublicRunStore } from '@/stores/public-run/public-run-checklist.store';
import { useAnalyticsRowSelectionStore } from '@/stores/responses/responses-row-selection.store';
import { useRunChecklistStore } from '@/stores/run/run-checklist.store';
import { OrgFile } from '@/types/chcklist-component.type';
import { PublicRun, PublicRunChecklist } from '@/types/public-run.type';
import {
  GuestOrgFile,
  PublicRunChecklistComment,
  PublicRunChecklistCommentFile
} from '@/types/run-checklist-comments.type';
import axios from 'axios';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { shallow } from 'zustand/shallow';
import { RunChecklistTree } from '../run-by-id.component';
import CLButton from '@/components/ui-controls/default-ui-controls/button';

interface CommentRowProps {
  comment: PublicRunChecklistComment;
  onDelete: (c: PublicRunChecklistComment) => void;
  isSingleResponse: boolean;
  runChecklistTree: RunChecklistTree; // you’ll need to pass this in
  isEditing: boolean;
  index: number;
}
interface PublicRunCommentsComponentProps {
  runChecklistId: string;
}

interface uploadedFileProps {
  file: File;
  previewUrl?: string;
  publicUrl?: string;
}

export default function PublicRunCommentsComponent({
  runChecklistId
}: PublicRunCommentsComponentProps) {
  // Variables

  // States
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [publicRunChecklistComments, setPublicRunChecklistComments] =
    useState<PublicRunChecklistComment[]>();
  const [showCommentText, setShowCommentText] = useState<boolean>(false);

  const [publicRunTaskComments, setPublicRunTaskComments] =
    useState<PublicRunChecklistComment[]>();
  const [checklistDetails, setChecklistDetails] = useState<
    PublicRunChecklistComment[]
  >([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [previewText, setPreviewText] = useState<string>('');
  const [mentionPrefix, setMentionPrefix] = useState('');
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionResults, setMentionResults] = useState<typeof allTasks>([]);
  const [selectedMentionTaskId, setSelectedMentionTaskId] = useState<
    string | null
  >(null);
  const [commentText, setCommentText] = useState<string>(' ');
  const [uploadedFiles, setUploadedFiles] = useState<uploadedFileProps[]>([]);
  const [isCommentSaving, setIsCommentSaving] = useState<boolean>(false);

  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [previewEditText, setPreviewEditText] = useState<string>('');

  // Store
  const [
    isPreview,
    isPublic,
    isSingleResponse,
    isStatusIndicatorDisabled,
    runChecklistTree
  ] = useRunChecklistStore(
    s => [
      s.isPreview,
      s.isPublic,
      s.isSingleResponse,
      s.isStatusIndicatorDisabled,
      s.runChecklistTree
    ],
    shallow
  );
  const [selectedOrganization] = useOrganizationStore(
    s => [s.selectedOrganization],
    shallow
  );
  const [publicRunAccessToken, orgGuestUser, publicRun] = usePublicRunStore(
    s => [s.publicRunAccessToken, s.orgGuestUser, s.publicRun],
    shallow
  );

  const [isPublicRun] = useAnalyticsRowSelectionStore(
    s => [s.isPublicRun],
    shallow
  );

  const allTasks = runChecklistTree?.checklist?.checklistsByChecklistId || [];

  // GraphQL

  useEffect(() => {
    () => {
      // Clear the data
      setPublicRunChecklistComments([]);
      setCommentText('');
      setPreviewText('');
      setPublicRunTaskComments([]);
      setUploadedFiles([]);
      setIsCommentSaving(false);
    };
  }, []);

  useEffect(() => {
    if (showCamera && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current
        .play()
        .catch(err => console.warn('Video playback failed:', err));
    }
  }, [showCamera, stream]);

  // Function to fetch data
  const fetchData = async () => {
    try {
      setIsLoading(true);
      const payload = {
        checklistId: publicRun?.id
      };
      const response = await axios.post(
        CommonConstants.API_PREFIX + '/public/run/fetch-comments',
        payload,
        {
          headers: {
            authorization: 'Bearer ' + publicRunAccessToken
          }
        }
      );
      if (response.data) {
        if (
          response.data?.data?.result2?.public_run_checklist_comments &&
          response.data?.data?.result2?.public_run_checklist_comments?.length >
            0
        ) {
          const allComments =
            response.data.data?.result2?.public_run_checklist_comments || [];

          // bucket A: comments on *this* checklist
          const checklistComments = allComments.filter(
            (c: { public_run_checklist_id: string }) =>
              c.public_run_checklist_id === runChecklistId
          );

          // bucket B: comments on *other* tasks
          const taskComments = allComments.filter(
            (c: { public_run_checklist_id: string }) =>
              c.public_run_checklist_id !== runChecklistId
          );

          setPublicRunChecklistComments(checklistComments);
          setPublicRunTaskComments(taskComments);
          setIsLoading(false);
        } else {
          setPublicRunChecklistComments([]);
          setIsLoading(false);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Call the fetch function when the component mounts
    fetchData();
  }, [runChecklistId]);

  async function insertPublicRunChecklistComment(
    insertablePublicRunChecklistComment: PublicRunChecklist[],
    guestOrgFiles: GuestOrgFile[] | undefined,
    publicRunChecklistCommentFiles: PublicRunChecklistCommentFile[] | undefined
  ) {
    try {
      // Update the payload
      const payload = {
        publicRunChecklistComment: insertablePublicRunChecklistComment,
        guestOrgFiles: guestOrgFiles,
        publicRunChecklistCommentFiles: publicRunChecklistCommentFiles
      };

      // Send the comment to database via api
      const response = await axios.post(
        CommonConstants.API_PREFIX + '/public/run/insert-comments',

        payload,
        {
          headers: {
            authorization: 'Bearer ' + publicRunAccessToken
          }
        }
      );
    } catch (error) {
      console.error('Error occurred while updating status:', error);
      throw error;
    }
  }

  // Kick off device camera
  async function openCamera() {
    try {
      const media = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      setStream(media);
      if (videoRef.current) {
        videoRef.current.srcObject = media;
        await videoRef.current.play();
      }
      setShowCamera(true);
    } catch (err) {
      console.error('Camera permission denied or error:', err);
      alert('Please allow camera access to capture image.');
    }
  }

  function closeCamera() {
    if (stream) {
      stream.getTracks().forEach(t => t.stop());
      setStream(null);
    }
    setShowCamera(false);
  }

  // Snap a photo and feed into upload handler
  function capturePhoto() {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(video, 0, 0);
    canvas.toBlob(blob => {
      if (blob) {
        // Create a File so our existing handleFileUpload logic can consume it
        const file = new File([blob], `camera-${Date.now()}.png`, {
          type: 'image/png'
        });
        handleFileUpload({
          target: { files: [file] } as any
        } as React.ChangeEvent<HTMLInputElement>);
      }
      closeCamera();
    }, 'image/png');
  }

  // 1) Recursively find the zero-based index path to a given taskId
  function findPath(
    node: RunChecklistTree,
    target: string,
    path: number[] = []
  ): number[] | null {
    if (node.checklist.run_checklist_task_id === target) return path;
    if (!node.children) return null;
    for (let i = 0; i < node.children.length; i++) {
      const child = node.children[i]!;
      const deeper = findPath(child, target, [...path, i]);
      if (deeper) return deeper;
    }
    return null;
  }

  // 2) Convert that to a “1.2.3.” style prefix
  function getTaskPrefix(taskId: string) {
    if (!runChecklistTree) return '';
    const path = findPath(runChecklistTree, taskId) ?? [];
    if (!path.length) return '';
    return path.map(i => (i + 1).toString()).join('.') + '. ';
  }

  function getMentionIndex(): string {
    if (!mentionPrefix || !selectedMentionTaskId) return '';
    // reuse your findPath logic from CommentRow
    const path = findPath(runChecklistTree!, selectedMentionTaskId) || [];
    if (path.length === 0) return '';
    return path.map(i => i + 1).join('.') + '.';
  }

  // escape helper
  const escapeHtml = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // build a regex that matches your prefix literally
  // we need to escape any regex-special chars in prefixNoSpace
  const escapeForRegExp = (s: string) =>
    s.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&');

  const mentionIndex = getMentionIndex();

  let highlighted = escapeHtml(previewText);

  if (mentionPrefix.trim()) {
    const prefixRe = new RegExp(
      `(${escapeForRegExp(mentionPrefix.trim())})`,
      'g'
    );
    highlighted = highlighted.replace(prefixRe, match => {
      // match === "@1.1. Identification…"
      const full = match.slice(1).trim(); // "1.1. Identification…"
      const index = getMentionIndex(); // "1.1."
      // remove the leading index from `full`
      const rawTitle = full.startsWith(index)
        ? full.slice(index.length).trim()
        : full;
      const escapedTitle = escapeHtml(rawTitle);

      // now you only inject one "@1.1. Title"
      return `<mark class="bg-yellow-100 rounded-md">@${index} ${escapedTitle} </mark>`;
    });
  }

  async function addComment(targetChecklistId?: string | null) {
    // Do a validation
    if (commentText || uploadedFiles.length > 0) {
      // Set the saving flag
      setIsCommentSaving(true);

      const newCommentId = uuidv4();
      const publicNewRunChecklistComment: PublicRunChecklistComment = {
        id: newCommentId,
        text: commentText || '',
        public_run_checklist_id: targetChecklistId || runChecklistId
      };

      const {
        guestOrgFiles,
        publicRunChecklistCommentFiles,
        publicRunChecklistCommentFilesForUI
      } = await uploadFilesToFileStorage(newCommentId);

      // If isPublic is true, update public checklist status
      await insertPublicRunChecklistComment(
        [publicNewRunChecklistComment],
        guestOrgFiles,
        publicRunChecklistCommentFiles
      );

      // Add the comment to the list, it will refresh the UI
      // if (checklistDetails) {
      //   setChecklistDetails([
      //     ...checklistDetails,
      //     {
      //       ...publicNewRunChecklistComment,
      //       ...{
      //         public_run_checklist_comment_files:
      //           publicRunChecklistCommentFilesForUI
      //       },
      //       ...{
      //         created_at: new Date().toISOString(),
      //         user: {
      //           id: orgGuestUser?.id,
      //           displayName: orgGuestUser?.full_name,
      //           email: orgGuestUser?.email,
      //           avatarUrl: orgGuestUser?.first_name
      //         }
      //       }
      //     }
      //   ]);
      // }

      if (publicRunChecklistComments && orgGuestUser) {
        setPublicRunChecklistComments([
          ...publicRunChecklistComments,
          {
            ...publicNewRunChecklistComment,
            ...{
              public_run_checklist_comment_files:
                publicRunChecklistCommentFilesForUI
            },
            ...{
              created_at: new Date().toISOString(),
              org_guest_user: {
                id: orgGuestUser?.id,
                full_name: orgGuestUser?.full_name,
                email: orgGuestUser?.email,
                first_name: orgGuestUser?.first_name
              }
            }
          }
        ]);
      }
      await fetchData();
      // Clear the comment text
      setSelectedMentionTaskId(null);
      setCommentText('');
      setPreviewText('');
      setUploadedFiles([]);
      setIsCommentSaving(false);
    }
  }

  async function deletePublicRunComment(
    deleteComment: PublicRunChecklistComment,
    commentIndex: number
  ) {
    try {
      const payload = {
        id: deleteComment.id
      };
      const response = await axios.post(
        CommonConstants.API_PREFIX + '/public/run/delete-comments',
        payload,
        {
          headers: {
            authorization: 'Bearer ' + publicRunAccessToken
          }
        }
      );

      if (response) {
        // Remove the comment from the list, it will refresh the UI
        if (publicRunChecklistComments) {
          setPublicRunChecklistComments([
            ...publicRunChecklistComments.slice(0, commentIndex),
            ...publicRunChecklistComments.slice(commentIndex + 1)
          ]);
        }
        await fetchData();
      }
    } catch (error) {
      if (error instanceof axios.AxiosError) {
        console.error(error?.response?.data?.error?.message || error.message);
      } else {
        console.error('Something went wrong. Please try again');
      }
    }
  }

  const onClickEdit = (comment: PublicRunChecklistComment) => {
    setEditingCommentId(comment.id as string);
    setPreviewEditText(comment.text as string);
    // Optionally focus the textarea:
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  const onClickCancel = () => {
    setEditingCommentId(null);
    setPreviewEditText('');
  };

  async function updatePublicRunComment(
    commentToUpdate: PublicRunChecklistComment,
    commentIndex: number
  ) {
    try {
      const payload = {
        id: commentToUpdate.id,
        text: previewEditText.trim()?.trim(),
        created_at: new Date().toISOString()
      };

      const response = await axios.post(
        CommonConstants.API_PREFIX + '/public/run/update-comments',
        payload,
        {
          headers: {
            authorization: 'Bearer ' + publicRunAccessToken
          }
        }
      );

      if (response && response.data) {
        // 1) Optimistically update the local list so the UI reflects the new text immediately:
        if (publicRunChecklistComments) {
          const updatedComments = [...publicRunChecklistComments];
          updatedComments[commentIndex] = {
            ...updatedComments[commentIndex],
            text: previewEditText?.trim(),
            // If the API returns an updated `created_at`, you can use it:
            created_at:
              response.data.data.update_public_run_checklist_comments_by_pk
                .created_at
          };
          setPublicRunChecklistComments(updatedComments);
        }

        // 2) Re-fetch server data to ensure everything is in sync
        await fetchData();
      }
    } catch (error) {
      if (error instanceof axios.AxiosError) {
        console.error(error.response?.data?.error?.message || error.message);
      } else {
        console.error('Something went wrong. Please try again');
      }
    } finally {
      setEditingCommentId(null);
      setPreviewEditText('');
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // if (e.key === 'Enter') {
    //   e.preventDefault();
    //   e.stopPropagation();
    //   addComment();
    // }
  };

  // Upload File
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const files = Array.from(event.target.files);

      // Iterate through each file and refresh the UI
      for (const file of files) {
        // Only for image add preview url
        uploadedFiles.push({
          file,
          previewUrl: file.type.includes('image')
            ? URL.createObjectURL(file)
            : ''
        });
      }
      setUploadedFiles([...uploadedFiles]);
    }
  };

  // Upload the files to storage
  const uploadFilesToFileStorage = async (newCommentId: string) => {
    const guestOrgFiles: GuestOrgFile[] = [];
    const publicRunChecklistCommentFiles: PublicRunChecklistCommentFile[] = [];
    const publicRunChecklistCommentFilesForUI: PublicRunChecklistCommentFile[] =
      [];

    // Iterate through each file and upload
    if (uploadedFiles && uploadedFiles.length > 0) {
      const uploadedFileInfos = await uploadFiles(uploadedFiles);
      if (uploadedFileInfos && uploadedFileInfos.fileMetadata) {
        for (const uploadedFile of uploadedFileInfos.fileMetadata) {
          if (
            uploadedFile.processedFiles &&
            uploadedFile.processedFiles.length > 0
          ) {
            try {
              const orgFileId = uuidv4();
              const runChecklistCommentFileId = uuidv4();

              // Prepare orgFile data
              const orgFile = {
                id: orgFileId,
                org_id: orgGuestUser?.org_id,
                file_id: uploadedFile.processedFiles[0].id
              };
              guestOrgFiles.push(orgFile);

              // Prepare runChecklistCommentFile data
              const runChecklistCommentFile = {
                id: runChecklistCommentFileId,
                url: uploadedFile.publicUrl,
                public_run_checklist_comment_id: newCommentId, // Assuming newCommentId is defined
                org_file_id: orgFileId
              };
              publicRunChecklistCommentFiles.push(runChecklistCommentFile);

              // Prepare runChecklistCommentFile for UI display
              const fileForUI = {
                ...runChecklistCommentFile,
                org_guest_file: {
                  ...orgFile,
                  file: {
                    id: uploadedFile.processedFiles[0].id,
                    name: uploadedFile.processedFiles[0].name,
                    mimeType: uploadedFile.processedFiles[0].mimeType
                  }
                }
              };
              publicRunChecklistCommentFilesForUI.push(fileForUI);
            } catch (error) {
              console.error('Error processing uploaded file:', error);
              // Handle error if needed (e.g., show error message)
            }
          }
        }
      }
    }

    return {
      guestOrgFiles,
      publicRunChecklistCommentFiles,
      publicRunChecklistCommentFilesForUI
    };
  };

  // Delete File
  const deleteUploadedFile = (index: number) => {
    uploadedFiles.splice(index, 1);
    setUploadedFiles([...uploadedFiles]);
  };

  // Upload API call
  const uploadFiles = async (uploadedFiles: any) => {
    const formData = new FormData();
    uploadedFiles.forEach((file: any, index: number) => {
      formData.append(`files`, file.file);
    });

    try {
      const response = await axios.post(
        CommonConstants.API_PREFIX + '/public/run/upload-files',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: 'Bearer ' + publicRunAccessToken
          }
        }
      );

      if (response && response.data) {
        return response.data;
      }
    } catch (error) {
      console.error('Error uploading files:', error);
    }
  };

  function CommentRow({
    comment,
    onDelete,
    isSingleResponse,
    runChecklistTree,
    isEditing,
    index
  }: CommentRowProps) {
    let taskId: string | null = null;
    const [localText, setLocalText] = useState(comment.text);
    const textareaEditRef = useRef<HTMLTextAreaElement>(null);

    if ((comment.public_run_checklist as PublicRun)?.task_id) {
      taskId = (comment.public_run_checklist as PublicRun)?.task_id as string;
    } else if (comment?.public_run_checklist_id) {
      taskId = comment?.public_run_checklist_id;
    }

    useEffect(() => {
      if (isEditing) {
        setLocalText(comment.text);
        setTimeout(() => textareaRef.current?.focus(), 0);
      }
    }, [isEditing, comment.text]);

    const onClickTaskEdit = (comment: PublicRunChecklistComment) => {
      setEditingCommentId(comment.id as string);
      setPreviewEditText(comment.text as string);
      // Optionally focus the textarea:
      setTimeout(() => {
        textareaEditRef.current?.focus();
      }, 0);
    };

    // Recursively search the tree for a node whose checklist.task_id === target,
    // returning the path of child‐indices that leads there.
    function findPath(
      node: RunChecklistTree,
      target: string,
      path: number[] = []
    ): number[] | null {
      if (node.checklist.task_id === target) {
        return path; // if root itself matches (unlikely), return []
      }
      if (!node.children) return null;

      for (let i = 0; i < node.children.length; i++) {
        const child = node.children[i]!;
        // if this child matches:
        if (child.checklist.task_id === target) {
          return [...path, i];
        }
        // else, dive deeper:
        const deeper = findPath(child, target, [...path, i]);
        if (deeper) return deeper;
      }
      return null;
    }

    // build our prefix + title
    const idxPath =
      taskId !== null
        ? findPath(runChecklistTree as RunChecklistTree, taskId) ?? []
        : [];
    // convert to 1-based prefix
    const prefix =
      idxPath.length > 0
        ? idxPath.map(i => (i + 1).toString()).join('.') + '.'
        : '';
    // traverse down to leaf to grab its title
    let taskTitle = '…';
    if (idxPath.length > 0) {
      let cursor: RunChecklistTree = runChecklistTree as RunChecklistTree;
      for (const i of idxPath) {
        cursor = cursor.children![i]!;
      }
      taskTitle = cursor.checklist.title as string;
    }

    async function updateLocalPublicRunComment(
      commentToUpdate: PublicRunChecklistComment,
      commentIndex: number
    ) {
      try {
        const payload = {
          id: commentToUpdate.id,
          text: localText?.trim(),
          created_at: new Date().toISOString()
        };

        const response = await axios.post(
          CommonConstants.API_PREFIX + '/public/run/update-comments',
          payload,
          {
            headers: {
              authorization: 'Bearer ' + publicRunAccessToken
            }
          }
        );

        if (response && response.data) {
          // 1) Optimistically update the local list so the UI reflects the new text immediately:
          if (publicRunChecklistComments) {
            const updatedComments = [...publicRunChecklistComments];
            updatedComments[commentIndex] = {
              ...updatedComments[commentIndex],
              text: previewEditText?.trim(),
              // If the API returns an updated `created_at`, you can use it:
              created_at:
                response.data.data.update_public_run_checklist_comments_by_pk
                  .created_at
            };
            setPublicRunChecklistComments(updatedComments);
          }

          // 2) Re-fetch server data to ensure everything is in sync
          await fetchData();
        }
      } catch (error) {
        if (error instanceof axios.AxiosError) {
          console.error(error.response?.data?.error?.message || error.message);
        } else {
          console.error('Something went wrong. Please try again');
        }
      } finally {
        setEditingCommentId(null);
        setPreviewEditText('');
      }
    }

    return (
      <>
        <div className="group gap-3 flex flex-col">
          <div className="flex flex-row items-center gap-2">
            {/* avatar */}
            <div className="mt-2 w-8 h-8 cl_primary_background rounded-full text-lg bg-slate-300 text-gray-900 flex items-center justify-center uppercase">
              {comment.org_guest_user?.full_name
                ? comment.org_guest_user.full_name[0]
                : comment.org_guest_user?.email![0]}
            </div>

            {/* header */}
            <div className="flex justify-between items-center w-full">
              {/* Left: Display name */}
              <div className="flex flex-row gap-2">
                <div className="text-sm font-normal">
                  {comment.org_guest_user?.full_name}
                </div>
                <div className="text-sm text-gray-600">
                  {convertISOToLocalDateString(comment.created_at as string)}
                </div>
              </div>

              {/* Right: Date and Delete */}
              <div className="flex items-center gap-2">
                {!isSingleResponse && !isEditing && (
                  <>
                    <CLTooltip content="Edit">
                      <CLCustomButton
                        className="hidden group-hover:block p-1 rounded-full hover:bg-slate-100"
                        onClick={() => {
                          onClickTaskEdit(comment);
                        }}
                      >
                        {/* SVG icon */}
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 18 18"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M15.8805 5.10904C16.277 4.71261 16.4998 4.17489 16.4999 3.61418C16.4999 3.05347 16.2773 2.5157 15.8808 2.11917C15.4844 1.72263 14.9467 1.49983 14.386 1.49976C13.8253 1.49969 13.2875 1.72236 12.891 2.11879L2.88145 12.1305C2.70732 12.3042 2.57854 12.5179 2.50645 12.753L1.5157 16.017C1.49632 16.0819 1.49485 16.1508 1.51146 16.2164C1.52807 16.2821 1.56214 16.342 1.61005 16.3898C1.65796 16.4376 1.71792 16.4716 1.78357 16.4881C1.84922 16.5046 1.91812 16.503 1.98295 16.4835L5.2477 15.4935C5.48258 15.4221 5.69633 15.2941 5.8702 15.1208L15.8805 5.10904Z"
                            stroke="#444444"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M11.25 3.75L14.25 6.75"
                            stroke="#444444"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </CLCustomButton>
                    </CLTooltip>
                    <CLCustomButton
                      title="Delete"
                      className="hidden group-hover:block p-1 rounded-full hover:bg-slate-100"
                      onClick={() => {
                        onDelete(comment);
                      }}
                    >
                      {/* SVG icon */}
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M15.0458 2.72184H10.8458V1.92918C10.8458 1.0921 10.1649 0.411185 9.32785 0.411185L6.67705 0.411133C5.83997 0.411133 5.15918 1.09212 5.15918 1.92913V2.72179L0.958912 2.72184C0.679592 2.72184 0.453125 2.94829 0.453125 3.22762C0.453125 3.50696 0.679578 3.73341 0.958912 3.73341H2.21246V12.2375C2.21246 14.0854 3.71579 15.5886 5.56353 15.5886H10.4411C12.2887 15.5886 13.7922 14.0853 13.7922 12.2375V3.73341H15.0457C15.3251 3.73341 15.5515 3.50696 15.5515 3.22762C15.5517 2.94825 15.3252 2.72184 15.0458 2.72184ZM6.17065 1.92918C6.17065 1.65001 6.39773 1.42288 6.67695 1.42288H9.32789C9.60706 1.42288 9.83419 1.64996 9.83419 1.92918V2.72184H6.17059L6.17065 1.92918ZM12.7808 12.2376C12.7808 13.5275 11.7313 14.5771 10.4413 14.5771H5.56358C4.27363 14.5771 3.22411 13.5276 3.22411 12.2376V3.73345H12.7805L12.7808 12.2376ZM9.22505 11.3746V6.61962C9.22505 6.3403 9.4515 6.11384 9.73083 6.11384C10.0102 6.11384 10.2366 6.34029 10.2366 6.61962V11.3746C10.2366 11.6539 10.0102 11.8803 9.73083 11.8803C9.45146 11.8806 9.22505 11.6539 9.22505 11.3746ZM5.76838 11.3746V6.61962C5.76838 6.3403 5.99483 6.11384 6.27417 6.11384C6.5535 6.11384 6.77995 6.34029 6.77995 6.61962V11.3746C6.77995 11.6539 6.5535 11.8803 6.27417 11.8803C5.99485 11.8806 5.76838 11.6539 5.76838 11.3746Z"
                          fill="#444444"
                        />
                      </svg>
                    </CLCustomButton>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="group flex gap-2 p-2 rounded-md pl-2 ml-8 bg-[#f1f5f9]">
            <div className="flex-1 flex flex-col gap-2">
              <div className="font-normal">
                {prefix && <span className="mr-1">{prefix}</span>}
                {taskTitle}
              </div>

              {/* body */}
              {!isEditing ? (
                <>
                  <div>{comment.text}</div>
                </>
              ) : (
                <>
                  <div className="flex flex-row gap-2 w-full">
                    {/* Text-editing area */}
                    <CLPrimaryCustomTextarea
                      ref={textareaEditRef}
                      style={{
                        backgroundColor: 'white',
                        outline: 'none',
                        boxShadow: 'none',
                        borderRadius: '5px',
                        lineHeight: '1',
                        padding: '0.5rem',
                        fontSize: '14px'
                      }}
                      className="w-full max-h-28 resize-none bg-transparent text-black caret-black"
                      placeholder="Edit comment... (Use @ to mention tasks)"
                      value={localText}
                      rows={1}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setLocalText(e.target.value)
                      }
                    />

                    {/* Buttons: cross (cancel) & checkmark (save) */}
                    <div className="flex justify-center items-center gap-2">
                      {/* Cross / Cancel */}
                      <CLCustomButton
                        className="p-1 rounded-full hover:bg-slate-100 bg-white border border-1 border-gray-700"
                        onClick={onClickCancel}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M18 6L6 18"
                            stroke="#444444"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M6 6L18 18"
                            stroke="#444444"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </CLCustomButton>

                      {/* Checkmark / Save */}
                      <CLCustomButton
                        className="p-1 rounded-full hover:bg-slate-100 bg-white border border-1 border-gray-700"
                        onClick={() =>
                          updateLocalPublicRunComment(comment, index)
                        }
                        disabled={localText?.trim().length === 0}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M20 6L9 17L4 12"
                            stroke="#444444"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </CLCustomButton>
                    </div>
                  </div>
                </>
              )}

              {/* attachments */}
              {(comment?.public_run_checklist_comment_files ?? []).length >
                0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                  {(comment.public_run_checklist_comment_files ?? []).map(
                    (f, i) => {
                      const mimeType = f.org_guest_file?.file?.mimeType ?? '';
                      const isImage = mimeType.startsWith('image/');
                      return (
                        <div
                          key={i}
                          className="flex items-center gap-2 border rounded-lg w-full max-w-full bg-white"
                        >
                          {/* Icon */}
                          <div
                            className={`flex items-center justify-center min-w-[44px] w-11 h-full rounded-l-lg ${
                              isImage ? 'bg-[#ffb9ff]' : 'bg-[#c8f7c5]'
                            }`}
                          >
                            {isImage ? (
                              // Image SVG
                              <svg
                                width="22"
                                height="22"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M4.94853 11.2038H11.1241C11.2309 11.2038 11.3066 11.1583 11.351 11.0673C11.3955 10.9762 11.3844 10.8884 11.3177 10.8038L9.64976 8.58845C9.59848 8.52178 9.53396 8.48845 9.45618 8.48845C9.3784 8.48845 9.31387 8.52178 9.26259 8.58845L7.44209 10.8961L6.27159 9.36023C6.21439 9.29356 6.14732 9.2613 6.07036 9.26345C5.9934 9.26558 5.92928 9.29998 5.87799 9.36665L4.76519 10.8038C4.70964 10.8927 4.7013 10.9816 4.74019 11.0705C4.77908 11.1594 4.84853 11.2038 4.94853 11.2038ZM3.29724 13.6666C3.03581 13.6666 2.81019 13.5717 2.62039 13.3819C2.43059 13.1921 2.33569 12.9665 2.33569 12.7051V3.29486C2.33569 3.03343 2.43059 2.80781 2.62039 2.61801C2.81019 2.42821 3.03581 2.33331 3.29724 2.33331H12.7074C12.9689 2.33331 13.1945 2.42821 13.3843 2.61801C13.5741 2.80781 13.669 3.03343 13.669 3.29486V12.7051C13.669 12.9665 13.5741 13.1921 13.3843 13.3819C13.1945 13.5717 12.9689 13.6666 12.7074 13.6666H3.29724ZM3.29724 12.9102H12.7074C12.7587 12.9102 12.8057 12.8888 12.8485 12.8461C12.8912 12.8034 12.9126 12.7564 12.9126 12.7051V3.29486C12.9126 3.24357 12.8912 3.19656 12.8485 3.15381C12.8057 3.11108 12.7587 3.08971 12.7074 3.08971H3.29724C3.24595 3.08971 3.19894 3.11108 3.15619 3.15381C3.11346 3.19656 3.09209 3.24357 3.09209 3.29486V12.7051C3.09209 12.7564 3.11346 12.8034 3.15619 12.8461C3.19894 12.8888 3.24595 12.9102 3.29724 12.9102Z"
                                  fill="black"
                                  fillOpacity="0.6"
                                />
                              </svg>
                            ) : (
                              // Default File SVG
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M10.502 1.5V4.5C10.502 4.89782 10.66 5.27936 10.9413 5.56066C11.2226 5.84196 11.6041 6 12.002 6H15.002M7.50195 6.75H6.00195M12.002 9.75H6.00195M12.002 12.75H6.00195M11.252 1.5H4.50195C4.10413 1.5 3.7226 1.65804 3.44129 1.93934C3.15999 2.22064 3.00195 2.60218 3.00195 3V15C3.00195 15.3978 3.15999 15.7794 3.44129 16.0607C3.7226 16.342 4.10413 16.5 4.50195 16.5H13.502C13.8998 16.5 14.2813 16.342 14.5626 16.0607C14.8439 15.7794 15.002 15.3978 15.002 15V5.25L11.252 1.5Z"
                                  stroke="black"
                                  strokeOpacity="0.6"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </div>
                          <div className="flex flex-row justify-between w-full overflow-hidden py-2">
                            <div
                              className="truncate  text-ellipsis"
                              title={f.org_guest_file?.file?.name}
                            >
                              {f.org_guest_file?.file?.name}
                            </div>
                            {/* Download Button */}
                            <div className="pr-2.5">
                              {
                                <CLTooltip content="Download" position="left">
                                  <button
                                    className=" bg-[#ffa726] p-1 rounded-full"
                                    onClick={() =>
                                      downloadFile(
                                        f.url!,
                                        f.org_guest_file?.file?.name
                                      )
                                    }
                                  >
                                    <svg
                                      width="12"
                                      height="12"
                                      viewBox="0 0 12 12"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M6.00195 8.5V1.5"
                                        stroke="black"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M3.00195 5.5L6.00195 8.5L9.00195 5.5"
                                        stroke="black"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M9.50195 10.5H2.50195"
                                        stroke="black"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </button>
                                </CLTooltip>
                              }
                            </div>
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {
        <>
          <hr
            className={
              `mb-4 border-t border-gray-300 flex-grow ` +
              (!showCommentText ? ' !mb-2' : '')
            }
          />
        </>
      }

      {/* Add Comment Button */}
      {!(publicRunTaskComments?.length || publicRunChecklistComments?.length) &&
        !isSingleResponse &&
        !showCommentText && (
          <div className="flex justify-start px-6 pt-2 pb-2">
            <CLPrimaryCustomButton
              className="!bg-white hover:!bg-gray-300 text-xs gap-2 !h-[38px] px-3 py-1 text-[14px]"
              onClick={() => setShowCommentText(true)} // Trigger the comment box to appear
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.002 10C14.002 10.3536 13.8615 10.6928 13.6114 10.9428C13.3614 11.1929 13.0222 11.3333 12.6686 11.3333H4.66862L2.00195 14V3.33333C2.00195 2.97971 2.14243 2.64057 2.39248 2.39052C2.64253 2.14048 2.98166 2 3.33529 2H12.6686C13.0222 2 13.3614 2.14048 13.6114 2.39052C13.8615 2.64057 14.002 2.97971 14.002 3.33333V10Z"
                  stroke="black"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              Add a Comment
            </CLPrimaryCustomButton>
          </div>
        )}

      {/* Add new comment */}
      {!isSingleResponse && showCommentText && !isLoading && (
        <>
          <div className="flex gap-2 px-4">
            {/* Profile image */}
            <div className="w-8 h-8 cl_primary_background rounded-full text-lg text-gray-900 flex items-center justify-center uppercase">
              {/* Profile image */}

              {/* Display name */}
              {orgGuestUser?.full_name && (
                <>{orgGuestUser?.full_name?.charAt(0)}</>
              )}

              {/* Email */}
              {!orgGuestUser?.full_name && orgGuestUser?.email && (
                <>{orgGuestUser?.email?.charAt(0)}</>
              )}
            </div>

            <div className="flex-1 flex flex-col gap-2">
              <div className="relative w-full border rounded-lg focus-within:border-yellow-400">
                <div
                  aria-hidden="true"
                  className="
                    absolute inset-0
                    text-base
                    m-4
                    mb-[7rem]
                    ml-[0.7rem]
                    mt-[0.7rem]
                    whitespace-pre-wrap
                    break-words
                    text-gray-900
                    pointer-events-none
                    rounded-md
                  "
                  // dangerously set HTML to get the <mark> tags rendered
                  dangerouslySetInnerHTML={{ __html: highlighted + '&nbsp;' }}
                />

                {/* Comment text area */}
                <CLPrimaryCustomTextarea
                  ref={textareaRef}
                  style={{
                    border: 'none',
                    outline: 'none',
                    boxShadow: 'none',
                    borderRadius: '0px',
                    lineHeight: '1.4',
                    padding: '0.75rem 1rem 0rem 1rem'
                  }}
                  className="w-full max-h-28 resize-none bg-transparent text-transparent caret-black"
                  placeholder={'Add a comment...(Use @ to mention tasks)'}
                  value={previewText}
                  rows={3}
                  onKeyDown={handleKeyDown}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const val = event.target.value;
                    setPreviewText(val);
                    if (mentionPrefix) {
                      // strip out the stored mentionPrefix only once (from the *start* of v)
                      const stripped = val.startsWith(mentionPrefix)
                        ? val.slice(mentionPrefix.length)
                        : val;
                      setCommentText(stripped);
                    } else {
                      setCommentText(val);
                    }

                    // detect a trailing “@…”
                    const m = val.match(/(?:^|\s)@([\w]*)$/);
                    if (m) {
                      const q = m[1].toLowerCase();
                      setMentionQuery(q);
                      // filter your tasks:

                      setMentionResults(
                        allTasks.filter(t => t.title?.toLowerCase().includes(q))
                      );
                    } else {
                      setMentionQuery(null);
                    }
                  }}
                ></CLPrimaryCustomTextarea>

                {mentionQuery !== null && mentionResults.length > 0 && (
                  <ul className="absolute bg-white left-0 bottom-32 mb-2 border rounded shadow-md max-h-28 overflow-y-auto z-20 w-[60%]">
                    {mentionResults.map((task, index) => {
                      const prefix = getTaskPrefix(
                        task.run_checklist_task_id as string
                      );

                      return (
                        <li
                          key={task.task_id}
                          className={`px-2 py-1 hover:bg-gray-100 cursor-pointer ${
                            index === 0 ? 'bg-gray-100' : ''
                          }`}
                          onMouseDown={e => {
                            e.preventDefault(); // keep focus in textarea

                            // 1) compute the numeric prefix + title
                            const idx = getTaskPrefix(
                              task.run_checklist_task_id as string
                            ); // e.g. "2.3. "
                            const fullMentionPrefix = `@${idx}${task.title} `; // "@2.3. My Task Title "

                            // 2) replace the trailing "@…" in previewText with your full mention
                            const fullMention = previewText.replace(
                              /@\S*$/,
                              fullMentionPrefix
                            );

                            // 3) strip out exactly that fullMentionPrefix from the start to get your comment body
                            const stripped = fullMention.startsWith(
                              fullMentionPrefix
                            )
                              ? fullMention.slice(fullMentionPrefix.length)
                              : fullMention;

                            // 4) update all the React state
                            setPreviewText(fullMention);
                            setCommentText(stripped);
                            setMentionPrefix(fullMentionPrefix);
                            setSelectedMentionTaskId(
                              task.run_checklist_task_id as string
                            );
                            setMentionQuery(null);
                          }}
                        >
                          {/* Show prefix (e.g. “2.3. ”) before the title */}
                          {prefix && (
                            <span className="font-medium">{prefix}</span>
                          )}
                          {task.title}
                        </li>
                      );
                    })}
                  </ul>
                )}

                {/* Attachment preview */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-2 gap-y-4 p-3 pb-0">
                  {uploadedFiles.map((uploadedFile, index) => (
                    <div key={index} className="flex items-start w-full">
                      <div className="group flex justify-between items-center gap-2 border border-black/10 rounded-lg pr-2 w-full">
                        {/* File data */}
                        <div className="flex gap-2 items-center overflow-hidden w-full">
                          {/* Icon */}
                          {uploadedFile?.previewUrl ? (
                            <img
                              src={uploadedFile.previewUrl}
                              className="w-32 h-16 rounded-l-lg"
                            />
                          ) : (
                            <div className="flex items-center justify-center w-24 h-16 rounded-l-lg bg-[#c8f7c5]">
                              <svg
                                height="50%"
                                width="50%"
                                viewBox="0 0 18 18"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M10.502 1.5V4.5C10.502 4.89782 10.66 5.27936 10.9413 5.56066C11.2226 5.84196 11.6041 6 12.002 6H15.002M7.50195 6.75H6.00195M12.002 9.75H6.00195M12.002 12.75H6.00195M11.252 1.5H4.50195C4.10413 1.5 3.7226 1.65804 3.44129 1.93934C3.15999 2.22064 3.00195 2.60218 3.00195 3V15C3.00195 15.3978 3.15999 15.7794 3.44129 16.0607C3.7226 16.342 4.10413 16.5 4.50195 16.5H13.502C13.8998 16.5 14.2813 16.342 14.5626 16.0607C14.8439 15.7794 15.002 15.3978 15.002 15V5.25L11.252 1.5Z"
                                  stroke="black"
                                  stroke-opacity="0.6"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>{' '}
                            </div>
                          )}

                          {/* Details */}
                          <div className="overflow-hidden flex flex-row items-center justify-between w-full py-2">
                            <div className="line-clamp-1">
                              {uploadedFile.file.name}
                            </div>
                            <div className="uppercase text-xs font-medium text-slate-600">
                              {`${uploadedFile.file.type}`.split('/')[1]}
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Delete icon */}
                      <div className="relative right-3 bottom-3">
                        <div
                          className="w-6 h-6 rounded-full bg-gray-600 cursor-pointer flex items-center justify-center"
                          onClick={() => {
                            deleteUploadedFile(index);
                          }}
                        >
                          <svg
                            width="8"
                            height="8"
                            viewBox="0 0 8 8"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M0.574173 0.575638C0.808488 0.341324 1.18839 0.341324 1.4227 0.575638L3.99844 3.15137L6.57417 0.575638C6.80849 0.341324 7.18839 0.341324 7.4227 0.575638C7.65702 0.809953 7.65702 1.18985 7.4227 1.42417L4.84697 3.9999L7.4227 6.57564C7.65702 6.80995 7.65702 7.18985 7.4227 7.42417C7.18839 7.65848 6.80849 7.65848 6.57417 7.42417L3.99844 4.84843L1.4227 7.42417C1.18839 7.65848 0.808488 7.65848 0.574173 7.42417C0.339859 7.18985 0.339859 6.80995 0.574173 6.57564L3.14991 3.9999L0.574173 1.42417C0.339859 1.18985 0.339859 0.809953 0.574173 0.575638Z"
                              fill="white"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <hr className="my-4 mb-0 border-t border-gray-300 w-full flex-grow" />
                <div className="flex justify-between px-4 py-3 pb-1">
                  {/* Left side */}
                  <div className="flex gap-2">
                    {/* Attachment icon */}
                    <CLTooltip content="Attachments">
                      <CLCustomButton>
                        <label htmlFor="comment-attachment-upload">
                          <svg
                            className="cursor-pointer rounded-full border border-gray-300 hover:bg-slate-100 p-1"
                            xmlns="http://www.w3.org/2000/svg"
                            height="28"
                            viewBox="0 -960 960 960"
                            width="28"
                          >
                            <path
                              d="M460-80q-92 0-156-64t-64-156v-420q0-66 47-113t113-47q66 0 113 47t47 113v380q0 42-29 71t-71 29q-42 0-71-29t-29-71v-350q0-13 8.5-21.5T390-720q13 0 21.5 8.5T420-690v350q0 17 11.5 28.5T460-300q17 0 28.5-11.5T500-340v-380q0-42-29-71t-71-29q-42 0-71 29t-29 71v420q0 66 47 113t113 47q66 0 113-47t47-113v-390q0-13 8.5-21.5T650-720q13 0 21.5 8.5T680-690v390q0 92-64 156T460-80Z"
                              fill="black"
                              fillOpacity="0.6"
                            />
                          </svg>
                        </label>
                        <input
                          className="hidden"
                          id="comment-attachment-upload"
                          type="file"
                          multiple
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            handleFileUpload(event);
                          }}
                        />
                      </CLCustomButton>
                    </CLTooltip>

                    {/* Capture Image icon */}
                    <CLTooltip content="Capture Image">
                      <CLCustomButton onClick={openCamera}>
                        {/* camera SVG */}
                        <svg
                          className="cursor-pointer rounded-full border border-gray-300 hover:bg-slate-100 p-1"
                          width="28"
                          height="28"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M11.75 4H8.25L6.5 6.0625H4.4C4.0287 6.0625 3.6726 6.20737 3.41005 6.46523C3.1475 6.72309 3 7.07283 3 7.4375V13.625C3 13.9897 3.1475 14.3394 3.41005 14.5973C3.6726 14.8551 4.0287 15 4.4 15H15.6C15.9713 15 16.3274 14.8551 16.5899 14.5973C16.8525 14.3394 17 13.9897 17 13.625V7.4375C17 7.07283 16.8525 6.72309 16.5899 6.46523C16.3274 6.20737 15.9713 6.0625 15.6 6.0625H13.5L11.75 4Z"
                            stroke="black"
                            stroke-opacity="0.6"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M10 12.25C11.1598 12.25 12.1 11.3266 12.1 10.1875C12.1 9.04841 11.1598 8.125 10 8.125C8.8402 8.125 7.9 9.04841 7.9 10.1875C7.9 11.3266 8.8402 12.25 10 12.25Z"
                            stroke="black"
                            stroke-opacity="0.6"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </CLCustomButton>
                    </CLTooltip>
                  </div>

                  {/* Right side */}
                  <div>
                    {/* Comment button */}
                    <CLPrimaryCustomButton
                      className="text-xs px-2 py-1 font-medium"
                      onClick={() => {
                        addComment(selectedMentionTaskId);
                      }}
                      disabled={
                        (!commentText && uploadedFiles?.length == 0) ||
                        isCommentSaving
                      }
                    >
                      Comment
                    </CLPrimaryCustomButton>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {showCamera && (
            <div
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
              onClick={closeCamera}
            >
              <div
                className="bg-white rounded-lg p-4 relative"
                onClick={e => e.stopPropagation()}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-auto h-auto bg-black rounded-md"
                />
                <div className="flex justify-end gap-2 mt-2">
                  <CLButton
                    className="px-4 py-2 bg-gray-200 rounded-full"
                    onClick={closeCamera}
                  >
                    Cancel
                  </CLButton>
                  <CLPrimaryCustomButton
                    className="px-2 py-1 font-medium"
                    onClick={capturePhoto}
                  >
                    Capture
                  </CLPrimaryCustomButton>
                </div>
                <canvas ref={canvasRef} className="hidden" />
              </div>
            </div>
          )}
        </>
      )}

      {publicRunTaskComments?.length || publicRunChecklistComments?.length ? (
        <>
          <div
            className={
              `flex items-start gap-4 px-5 ` +
              (showCommentText ? 'flex-col' : 'flex-row !items-center')
            }
          >
            {showCommentText && (
              <hr className="mr-4 w-full my-4 mb-0 border-t border-gray-300 flex-grow" />
            )}
            <h3 className={`text-gray-600 whitespace-nowrap ml-0 mb-3`}>
              Comments (
              {(publicRunChecklistComments?.length || 0) +
                (publicRunTaskComments?.length || 0)}
              )
            </h3>

            {/* Add Comment Button */}
            {!isSingleResponse && !showCommentText && (
              <div className="flex justify-start px-0 pb-3">
                <CLPrimaryCustomButton
                  className="!bg-white hover:!bg-gray-300 text-xs gap-2 !h-[32px] px-3 py-1 text-[14px]"
                  onClick={() => setShowCommentText(true)} // Trigger the comment box to appear
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14.002 10C14.002 10.3536 13.8615 10.6928 13.6114 10.9428C13.3614 11.1929 13.0222 11.3333 12.6686 11.3333H4.66862L2.00195 14V3.33333C2.00195 2.97971 2.14243 2.64057 2.39248 2.39052C2.64253 2.14048 2.98166 2 3.33529 2H12.6686C13.0222 2 13.3614 2.14048 13.6114 2.39052C13.8615 2.64057 14.002 2.97971 14.002 3.33333V10Z"
                      stroke="black"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Add a Comment
                </CLPrimaryCustomButton>
              </div>
            )}
          </div>

          <div
            className="gap-2 mx-4 flex flex-col p-4 mb-4 rounded-md"
            style={{ boxShadow: '0 0 8px rgba(0,0,0,0.1)' }}
          >
            {isLoading && (
              <div className="flex gap-2 py-4 animate-pulse">
                {/* Profile image */}
                <div className="w-8 h-8 bg-gray-200 rounded-full"></div>

                {/* Comment block */}
                <div className="flex-1 flex flex-col gap-2">
                  {/* Name */}
                  <div className="w-2/4 h-4 bg-gray-200 rounded"></div>

                  {/* Text */}
                  <div className="h-20 bg-gray-200 rounded"></div>
                </div>
              </div>
            )}

            {/* List of comments */}
            {!isLoading && (
              <>
                <>
                  {/*List of Task Comments */}
                  {publicRunTaskComments?.length ? (
                    <div className="flex flex-col gap-4 mb-4">
                      {publicRunTaskComments.map((comment, index) => {
                        return (
                          <CommentRow
                            key={comment.id}
                            comment={comment}
                            runChecklistTree={
                              runChecklistTree as RunChecklistTree
                            }
                            onDelete={c => deletePublicRunComment(c, index)}
                            isSingleResponse={isSingleResponse}
                            isEditing={editingCommentId === comment.id}
                            index={index}
                          />
                        );
                      })}
                    </div>
                  ) : null}
                </>

                {/* List of Checklist comments */}
                <div className="flex flex-col gap-4 mb-4">
                  {publicRunChecklistComments?.map((comment, commentIndex) => {
                    const isEditing = editingCommentId === comment.id;
                    return (
                      <div key={comment.id} className="group flex gap-2">
                        {/* Each comment */}

                        {/* Profile image */}
                        <div className="w-8 h-8 cl_primary_background rounded-full text-lg text-gray-900 flex items-center justify-center uppercase">
                          {/* Profile image */}
                          {/* Icon */}
                          {/* {comment?.user?.avatarUrl && (
                            <>
                              <img
                                className="w-8 h-8 rounded-full mx-auto bg-slate-300"
                                src={comment?.user?.avatarUrl}
                                alt=""
                              />
                            </>
                          )} */}

                          {/* Display name */}
                          {comment?.org_guest_user &&
                            comment?.org_guest_user?.full_name && (
                              <>
                                {comment.org_guest_user?.full_name?.charAt(0)}
                              </>
                            )}

                          {/* Email */}
                          {!comment?.org_guest_user?.first_name &&
                            comment.org_guest_user?.email && (
                              <>{comment.org_guest_user?.email?.charAt(0)}</>
                            )}
                        </div>

                        {/* Comment block */}
                        <div className="flex-1 flex flex-col gap-2">
                          {/* Header */}
                          <div className="flex justify-between min-h-[22px]">
                            {/* User info */}
                            <div className="flex">
                              <div className="font-normal">
                                {comment.org_guest_user?.full_name}
                              </div>
                              <div className="pl-2 font-medium text-gray-400">
                                {convertISOToLocalDateString(
                                  comment?.created_at || ''
                                )}
                              </div>
                            </div>

                            {/* Delete button */}
                            <div>
                              <div className="flex items-center gap-2">
                                {!isSingleResponse && !isEditing && (
                                  <>
                                    <CLTooltip content="Edit">
                                      <CLCustomButton
                                        className="hidden group-hover:block p-1 rounded-full hover:bg-slate-100"
                                        onClick={() => {
                                          onClickEdit(comment);
                                        }}
                                      >
                                        {/* SVG icon */}
                                        <svg
                                          width="14"
                                          height="14"
                                          viewBox="0 0 18 18"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M15.8805 5.10904C16.277 4.71261 16.4998 4.17489 16.4999 3.61418C16.4999 3.05347 16.2773 2.5157 15.8808 2.11917C15.4844 1.72263 14.9467 1.49983 14.386 1.49976C13.8253 1.49969 13.2875 1.72236 12.891 2.11879L2.88145 12.1305C2.70732 12.3042 2.57854 12.5179 2.50645 12.753L1.5157 16.017C1.49632 16.0819 1.49485 16.1508 1.51146 16.2164C1.52807 16.2821 1.56214 16.342 1.61005 16.3898C1.65796 16.4376 1.71792 16.4716 1.78357 16.4881C1.84922 16.5046 1.91812 16.503 1.98295 16.4835L5.2477 15.4935C5.48258 15.4221 5.69633 15.2941 5.8702 15.1208L15.8805 5.10904Z"
                                            stroke="#444444"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                          <path
                                            d="M11.25 3.75L14.25 6.75"
                                            stroke="#444444"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      </CLCustomButton>
                                    </CLTooltip>
                                    <CLTooltip content="Delete">
                                      <CLCustomButton
                                        title="Delete"
                                        className="hidden group-hover:block p-1 rounded-full hover:bg-slate-100 "
                                        onClick={() => {
                                          deletePublicRunComment(
                                            comment,
                                            commentIndex
                                          );
                                        }}
                                      >
                                        <svg
                                          width="14"
                                          height="14"
                                          viewBox="0 0 16 16"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M15.0458 2.72184H10.8458V1.92918C10.8458 1.0921 10.1649 0.411185 9.32785 0.411185L6.67705 0.411133C5.83997 0.411133 5.15918 1.09212 5.15918 1.92913V2.72179L0.958912 2.72184C0.679592 2.72184 0.453125 2.94829 0.453125 3.22762C0.453125 3.50696 0.679578 3.73341 0.958912 3.73341H2.21246V12.2375C2.21246 14.0854 3.71579 15.5886 5.56353 15.5886H10.4411C12.2887 15.5886 13.7922 14.0853 13.7922 12.2375V3.73341H15.0457C15.3251 3.73341 15.5515 3.50696 15.5515 3.22762C15.5517 2.94825 15.3252 2.72184 15.0458 2.72184ZM6.17065 1.92918C6.17065 1.65001 6.39773 1.42288 6.67695 1.42288H9.32789C9.60706 1.42288 9.83419 1.64996 9.83419 1.92918V2.72184H6.17059L6.17065 1.92918ZM12.7808 12.2376C12.7808 13.5275 11.7313 14.5771 10.4413 14.5771H5.56358C4.27363 14.5771 3.22411 13.5276 3.22411 12.2376V3.73345H12.7805L12.7808 12.2376ZM9.22505 11.3746V6.61962C9.22505 6.3403 9.4515 6.11384 9.73083 6.11384C10.0102 6.11384 10.2366 6.34029 10.2366 6.61962V11.3746C10.2366 11.6539 10.0102 11.8803 9.73083 11.8803C9.45146 11.8806 9.22505 11.6539 9.22505 11.3746ZM5.76838 11.3746V6.61962C5.76838 6.3403 5.99483 6.11384 6.27417 6.11384C6.5535 6.11384 6.77995 6.34029 6.77995 6.61962V11.3746C6.77995 11.6539 6.5535 11.8803 6.27417 11.8803C5.99485 11.8806 5.76838 11.6539 5.76838 11.3746Z"
                                            fill="#444444"
                                          />
                                        </svg>
                                      </CLCustomButton>
                                    </CLTooltip>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Text */}
                          {!isEditing ? (
                            <>
                              <div className="font-normal">{comment.text}</div>
                            </>
                          ) : (
                            <>
                              <div className="flex flex-row gap-2 w-full">
                                {/* Text-editing area */}
                                <CLPrimaryCustomTextarea
                                  ref={textareaRef}
                                  style={{
                                    outline: 'none',
                                    boxShadow: 'none',
                                    borderRadius: '5px',
                                    lineHeight: '1',
                                    padding: '0.5rem',
                                    fontSize: '14px'
                                  }}
                                  className="w-full resize-none max-h-28 bg-transparent text-black caret-black"
                                  placeholder="Edit comment... (Use @ to mention tasks)"
                                  value={previewEditText}
                                  rows={1}
                                  onChange={(
                                    e: React.ChangeEvent<HTMLTextAreaElement>
                                  ) => setPreviewEditText(e.target.value)}
                                />

                                {/* Buttons: cross (cancel) & checkmark (save) */}
                                <div className="flex justify-center items-center gap-2">
                                  {/* Cross / Cancel */}
                                  <CLCustomButton
                                    className="p-1 rounded-full hover:bg-slate-100 border border-1 border-gray-700"
                                    onClick={onClickCancel}
                                  >
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M18 6L6 18"
                                        stroke="#444444"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M6 6L18 18"
                                        stroke="#444444"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </CLCustomButton>

                                  {/* Checkmark / Save */}
                                  <CLCustomButton
                                    className="p-1 rounded-full hover:bg-slate-100 border border-1 border-gray-700"
                                    onClick={() =>
                                      updatePublicRunComment(
                                        comment,
                                        commentIndex
                                      )
                                    }
                                    disabled={
                                      previewEditText.trim().length === 0
                                    }
                                  >
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M20 6L9 17L4 12"
                                        stroke="#444444"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </CLCustomButton>
                                </div>
                              </div>
                            </>
                          )}

                          {/* Attachments */}
                          {comment?.public_run_checklist_comment_files &&
                            comment?.public_run_checklist_comment_files
                              ?.length > 0 && (
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                                {comment.public_run_checklist_comment_files.map(
                                  (attachmentFile, index) => {
                                    const mimeType =
                                      attachmentFile.org_guest_file?.file
                                        ?.mimeType ?? '';
                                    const isImage =
                                      mimeType.startsWith('image/');

                                    return (
                                      <div key={index}>
                                        {/* File data */}
                                        <div className="group flex justify-between items-center gap-2 border border-black/10 rounded-lg">
                                          <div className="flex gap-2 items-center overflow-hidden w-full">
                                            {/* Icon */}
                                            <div
                                              className={`flex items-center justify-center min-w-[44px] w-11 h-10 rounded-l-lg ${
                                                isImage
                                                  ? 'bg-[#ffb9ff]'
                                                  : 'bg-[#c8f7c5]'
                                              }`}
                                            >
                                              {isImage ? (
                                                // Image SVG
                                                <svg
                                                  width="22"
                                                  height="22"
                                                  viewBox="0 0 16 16"
                                                  fill="none"
                                                  xmlns="http://www.w3.org/2000/svg"
                                                >
                                                  <path
                                                    d="M4.94853 11.2038H11.1241C11.2309 11.2038 11.3066 11.1583 11.351 11.0673C11.3955 10.9762 11.3844 10.8884 11.3177 10.8038L9.64976 8.58845C9.59848 8.52178 9.53396 8.48845 9.45618 8.48845C9.3784 8.48845 9.31387 8.52178 9.26259 8.58845L7.44209 10.8961L6.27159 9.36023C6.21439 9.29356 6.14732 9.2613 6.07036 9.26345C5.9934 9.26558 5.92928 9.29998 5.87799 9.36665L4.76519 10.8038C4.70964 10.8927 4.7013 10.9816 4.74019 11.0705C4.77908 11.1594 4.84853 11.2038 4.94853 11.2038ZM3.29724 13.6666C3.03581 13.6666 2.81019 13.5717 2.62039 13.3819C2.43059 13.1921 2.33569 12.9665 2.33569 12.7051V3.29486C2.33569 3.03343 2.43059 2.80781 2.62039 2.61801C2.81019 2.42821 3.03581 2.33331 3.29724 2.33331H12.7074C12.9689 2.33331 13.1945 2.42821 13.3843 2.61801C13.5741 2.80781 13.669 3.03343 13.669 3.29486V12.7051C13.669 12.9665 13.5741 13.1921 13.3843 13.3819C13.1945 13.5717 12.9689 13.6666 12.7074 13.6666H3.29724ZM3.29724 12.9102H12.7074C12.7587 12.9102 12.8057 12.8888 12.8485 12.8461C12.8912 12.8034 12.9126 12.7564 12.9126 12.7051V3.29486C12.9126 3.24357 12.8912 3.19656 12.8485 3.15381C12.8057 3.11108 12.7587 3.08971 12.7074 3.08971H3.29724C3.24595 3.08971 3.19894 3.11108 3.15619 3.15381C3.11346 3.19656 3.09209 3.24357 3.09209 3.29486V12.7051C3.09209 12.7564 3.11346 12.8034 3.15619 12.8461C3.19894 12.8888 3.24595 12.9102 3.29724 12.9102Z"
                                                    fill="black"
                                                    fillOpacity="0.6"
                                                  />
                                                </svg>
                                              ) : (
                                                // Default File SVG
                                                <svg
                                                  width="18"
                                                  height="18"
                                                  viewBox="0 0 18 18"
                                                  fill="none"
                                                  xmlns="http://www.w3.org/2000/svg"
                                                >
                                                  <path
                                                    d="M10.502 1.5V4.5C10.502 4.89782 10.66 5.27936 10.9413 5.56066C11.2226 5.84196 11.6041 6 12.002 6H15.002M7.50195 6.75H6.00195M12.002 9.75H6.00195M12.002 12.75H6.00195M11.252 1.5H4.50195C4.10413 1.5 3.7226 1.65804 3.44129 1.93934C3.15999 2.22064 3.00195 2.60218 3.00195 3V15C3.00195 15.3978 3.15999 15.7794 3.44129 16.0607C3.7226 16.342 4.10413 16.5 4.50195 16.5H13.502C13.8998 16.5 14.2813 16.342 14.5626 16.0607C14.8439 15.7794 15.002 15.3978 15.002 15V5.25L11.252 1.5Z"
                                                    stroke="black"
                                                    strokeOpacity="0.6"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                  />
                                                </svg>
                                              )}
                                            </div>

                                            {/* Details */}
                                            <div className="overflow-hidden flex flex-row justify-between w-full py-2">
                                              <div
                                                className="line-clamp-1"
                                                title={
                                                  attachmentFile?.org_guest_file
                                                    ?.file?.name
                                                }
                                              >
                                                {
                                                  attachmentFile?.org_guest_file
                                                    ?.file?.name
                                                }
                                              </div>
                                              <div className="flex items-center gap-2">
                                                {/* File type */}
                                                {/* <div className="uppercase text-xs	font-medium	text-slate-600	">
                                                  {
                                                    `${attachmentFile?.org_file?.file?.mimeType}`.split(
                                                      '/'
                                                    )[1]
                                                  }
                                                </div> */}

                                                {/* Download */}
                                                <div className="pr-2.5">
                                                  {
                                                    <CLTooltip
                                                      content="Download"
                                                      position="left"
                                                    >
                                                      <button
                                                        className=" bg-[#ffa726] p-1 rounded-full"
                                                        onClick={() => {
                                                          downloadFile(
                                                            attachmentFile.url as string,
                                                            attachmentFile
                                                              ?.org_guest_file
                                                              ?.file?.name
                                                          );
                                                        }}
                                                      >
                                                        <svg
                                                          width="12"
                                                          height="12"
                                                          viewBox="0 0 12 12"
                                                          fill="none"
                                                          xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                          <path
                                                            d="M6.00195 8.5V1.5"
                                                            stroke="black"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                          />
                                                          <path
                                                            d="M3.00195 5.5L6.00195 8.5L9.00195 5.5"
                                                            stroke="black"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                          />
                                                          <path
                                                            d="M9.50195 10.5H2.50195"
                                                            stroke="black"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                          />
                                                        </svg>
                                                      </button>
                                                    </CLTooltip>
                                                  }
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                            )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </>
      ) : null}
    </>
  );
}
